// Tarjeta de un modelo en «Elige el teléfono». Sin variante elegida muestra «Desde» y el chip que
// abre la matriz; con variante, su precio, promos y «Elegir», que aparta ese SKU.
import { useState } from 'react';
import { Check, ChevronDown, Grid3x3, Smartphone } from 'lucide-react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import type { ModelGroup } from '@/domains/sales/portal/catalog/lib/modelGroups';
import { variantLabel } from '@/shared/lib/format/capacity';
import type { LevelCreditTerms } from '../cardBenefits';
import { isSoldOut } from '../catalogAvailability';
import type { BenefitBubble, CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';
import { CardActions, CardPrice, CardPromoList, cardPromosOf, type CardPromo } from './CardPricing';
import { HighlightedText } from './HighlightedText';
import { StockChip } from './StockChip';
import { VariantMatrixDialog } from './VariantMatrixDialog';

const NO_SALE_BENEFITS: CardSaleBenefits = { bubbles: [], discountedPriceCents: null };
const NO_TERMS: LevelCreditTerms = {};
const CHIP =
    'inline-flex h-7 min-w-0 items-center gap-1.5 rounded-md border border-border bg-muted/60 px-2 text-[12px] font-semibold transition-colors hover:border-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function ModelCard({
    group,
    variant,
    selected,
    terms,
    busy = false,
    reservedSku = null,
    onSelect,
    onVariantChange,
    onChoose,
    saleBenefits = NO_SALE_BENEFITS,
    creditTerms = NO_TERMS,
    creditBubbles = [],
    promos,
}: {
    group: ModelGroup;
    /** La variante elegida; `null` si nadie la ha elegido y la tarjeta muestra «Desde». */
    variant: CatalogItem | null;
    /** La tarjeta es la del detalle. */
    selected: boolean;
    /** Términos de la búsqueda, para resaltar la coincidencia. */
    terms: string[];
    busy?: boolean;
    /** SKU de la línea de la sesión: se elige aunque ya no tenga piezas. */
    reservedSku?: string | null;
    /** Tocar el nombre muestra el detalle de la variante (o de la más barata), sin apartar. */
    onSelect: (item: CatalogItem) => void;
    /** Se eligió una celda de la matriz. */
    onVariantChange: (item: CatalogItem) => void;
    /** «Elegir» de la tarjeta. */
    onChoose: (item: CatalogItem) => void;
    /** Precio con descuento de la variante elegida o, sin ella, de `group.defaultVariant`. */
    saleBenefits?: CardSaleBenefits;
    creditTerms?: LevelCreditTerms;
    creditBubbles?: readonly BenefitBubble[];
    /** Lista de promos de la tarjeta; sin ella salen las de `saleBenefits` y `creditBubbles`. */
    promos?: readonly CardPromo[];
}) {
    const [matrixOpen, setMatrixOpen] = useState(false);
    const single = group.variants.length === 1;
    const chosen = single ? group.variants[0] : variant;
    const shown = chosen ?? group.defaultVariant;
    const blocked = isSoldOut(shown) && shown.sku !== reservedSku;
    // Agotado salvo que una de sus variantes sea la que ya tiene apartada la venta.
    const soldOut = group.soldOut && !group.variants.some((v) => v.sku === reservedSku);
    const list = cardPromosOf(promos, saleBenefits, creditBubbles);

    return (
        <article
            aria-label={`${group.brand} ${group.model}`}
            className={cn(
                'relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card transition-all',
                selected
                    ? 'border-foreground shadow-[0_0_0_1px_hsl(var(--foreground))]'
                    : 'border-border',
                soldOut && 'opacity-[0.52]',
            )}
        >
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-4 pb-3.5 pt-4">
                <span className="flex min-h-5 items-center gap-1.5 pr-6 text-muted-foreground">
                    <Smartphone className="size-4 flex-none" aria-hidden />
                    <span className="min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.06em]">
                        <HighlightedText text={group.brand} terms={terms} /> · Teléfono
                    </span>
                </span>
                {selected && (
                    <span className="absolute right-3 top-3 flex size-[22px] items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="size-3" aria-hidden />
                    </span>
                )}
                <span className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
                    <button
                        type="button"
                        aria-pressed={selected}
                        disabled={soldOut}
                        onClick={() => {
                            onSelect(shown);
                        }}
                        title={group.model}
                        className="min-w-0 truncate rounded-sm text-left text-[16px] font-bold leading-tight hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:no-underline"
                    >
                        <HighlightedText text={group.model} terms={terms} />
                    </button>
                    {chosen !== null && (
                        <StockChip item={chosen} className="flex-none whitespace-nowrap" />
                    )}
                    {chosen === null && group.soldOut && (
                        <StockChip item={{ stock: 0 }} className="flex-none whitespace-nowrap" />
                    )}
                    {!single && chosen === null && (
                        <button
                            type="button"
                            aria-label={`Elegir variante de ${group.model}`}
                            onClick={() => {
                                setMatrixOpen(true);
                            }}
                            className={CHIP}
                        >
                            <Grid3x3 className="size-3.5 flex-none" aria-hidden />
                            Elegir
                        </button>
                    )}
                </span>

                {single && (
                    <span className="min-h-[18px] truncate text-[12px] text-muted-foreground">
                        {variantLabel(shown)}
                    </span>
                )}
                {!single && chosen !== null && (
                    <button
                        type="button"
                        aria-label={`Cambiar variante: ${variantLabel(chosen)}`}
                        onClick={() => {
                            setMatrixOpen(true);
                        }}
                        className={cn(CHIP, 'mt-1 self-start')}
                    >
                        <Grid3x3 className="size-3.5 flex-none" aria-hidden />
                        <span className="min-w-0 truncate">{variantLabel(chosen)}</span>
                        <span className="flex-none font-normal text-muted-foreground">
                            · cambiar
                        </span>
                        <ChevronDown
                            className="size-3.5 flex-none text-muted-foreground"
                            aria-hidden
                        />
                    </button>
                )}

                <CardPrice
                    item={shown}
                    listCents={shown.cashPriceCents}
                    saleBenefits={saleBenefits}
                    creditTerms={creditTerms}
                    from={chosen === null}
                />
            </div>

            <CardPromoList promos={list} />
            {chosen !== null && (
                <CardActions>
                    <Button
                        size="sm"
                        disabled={blocked || busy}
                        onClick={() => {
                            onChoose(chosen);
                        }}
                    >
                        {blocked ? 'Sin piezas' : 'Elegir'}
                    </Button>
                </CardActions>
            )}

            {!single && (
                <VariantMatrixDialog
                    group={group}
                    open={matrixOpen}
                    selectedSku={chosen?.sku ?? null}
                    reservedSku={reservedSku}
                    onClose={() => {
                        setMatrixOpen(false);
                    }}
                    onPick={(item) => {
                        setMatrixOpen(false);
                        onVariantChange(item);
                    }}
                />
            )}
        </article>
    );
}
